import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable } from 'rxjs';
import { ProductCard } from '../../components/shared/product-card/product-card';
import { ProductCardSkeleton } from '../../components/shared/product-card-skeleton/product-card-skeleton';
import { OfferBanner } from '../../components/shared/offer-banner/offer-banner';
import { Product, DoseType } from '../../interfaces/product.interface';
import { ProductService } from '../../services/product.service';
import { CartService } from '../../services/cart.service';
import { DoseColorService } from '../../services/dose-color.service';
import { LanguageService } from '../../services/language.service';
import { SiteContentService, HomeAssets } from '../../services/site-content.service';
import { fadeInUp } from '../../animations/route-animations';
import { combineLatest } from 'rxjs';

interface Feedback {
  id: number;
  name: string;
  location: string;
  avatar: string;
  text: string;
}

@Component({
  selector: 'app-home',
  imports: [CommonModule, ProductCard, ProductCardSkeleton, OfferBanner],
  // imports: [CommonModule, ProductCard],
  templateUrl: './home.html',
  styleUrl: './home.scss',
  animations: [fadeInUp]
})
export class Home implements OnInit, OnDestroy {
  products: Product[] = [];
  isLoadingProducts = true;
  currentFeedbackIndex = 0;
  private autoPlayInterval: any;
  private isPaused = false;
  
  feedbacks: Feedback[] = [];
  
  // Site content properties
  homeAssets: HomeAssets | null = null;
  currentLanguage = 'en';
  bannerImageUrl = '';
  offerImages: string[] = []; // Changed to array for multiple offers
  
  // Carousel properties
  currentOfferIndex = 0;
  private offerCarouselInterval: any;

  constructor(
    private productService: ProductService,
    private cartService: CartService,
    private doseColorService: DoseColorService,
    private languageService: LanguageService,
    private siteContentService: SiteContentService
  ) {}

  ngOnInit(): void {
    // Subscribe to products first (shared observable emits immediately if cached)
    this.productService.getProducts().subscribe(products => {
      this.products = products;
      // Hide loading when products are received
      if (products.length > 0) {
        this.isLoadingProducts = false;
      }
    });

    // Subscribe to loading state from service (fallback)
    this.productService.productsLoading$.subscribe(loading => {
      this.isLoadingProducts = loading;
    });
    
    // Load site content assets
    this.loadSiteContent();
    
    // Listen to language changes
    this.languageService.currentLanguage$.subscribe(lang => {
      this.currentLanguage = lang;
      this.updateImagesForLanguage();
    });
    
    // Rebuild feedbacks whenever language or translations change to avoid showing raw keys
    combineLatest([
      this.languageService.currentLanguage$,
      this.languageService.translations$
    ]).subscribe(() => {
      this.feedbacks = this.buildFeedbacks();
    });
    // Start auto-play
    this.startAutoPlay();
    // Start offer carousel
    this.startOfferCarousel();
  }

  ngOnDestroy(): void {
    this.stopAutoPlay();
    this.stopOfferCarousel();
  }

  addToCart(product: Product) {
    this.cartService.addToCart(product);
    // You could add a toast notification here
  }

  getGradientForProduct(product: Product): string {
    try {
      if (!product?.doseType) {
        return 'linear-gradient(135deg, #6c757d 0%, #495057 100%)'; // Default gradient
      }
      return this.doseColorService.getDoseGradient(product.doseType);
    } catch (error) {
      console.warn('Error getting gradient for product:', error);
      return 'linear-gradient(135deg, #6c757d 0%, #495057 100%)'; // Fallback
    }
  }

  // Add safe product filtering
  getSafeProducts(): Product[] {
    try {
      return this.products?.filter(p => p && p.doseType) || [];
    } catch (error) {
      console.warn('Error filtering products:', error);
      return [];
    }
  }

  // Carousel navigation methods
  previousFeedback(): void {
    this.currentFeedbackIndex = this.currentFeedbackIndex === 0 
      ? this.feedbacks.length - 1 
      : this.currentFeedbackIndex - 1;
    this.resetAutoPlay();
  }

  nextFeedback(): void {
    this.currentFeedbackIndex = this.currentFeedbackIndex === this.feedbacks.length - 1 
      ? 0 
      : this.currentFeedbackIndex + 1;
    this.resetAutoPlay();
  }

  goToFeedback(index: number): void {
    this.currentFeedbackIndex = index;
    this.resetAutoPlay();
  }

  // Auto-play functionality
  startAutoPlay(): void {
    this.autoPlayInterval = setInterval(() => {
      if (!this.isPaused) {
        this.nextFeedback();
      }
    }, 5000); // Change feedback every 5 seconds
  }

  stopAutoPlay(): void {
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
      this.autoPlayInterval = null;
    }
  }

  resetAutoPlay(): void {
    this.stopAutoPlay();
    this.startAutoPlay();
  }

  pauseAutoPlay(): void {
    this.isPaused = true;
  }

  resumeAutoPlay(): void {
    this.isPaused = false;
  }

  // Translation method
  t(key: string, params?: { [key: string]: string | number }): string {
    return this.languageService.t(key, params);
  }

  private buildFeedbacks(): Feedback[] {
    return [
      {
        id: 1,
        name: 'Ravindra Fadtare',
        location: 'Satara, Maharashtra',
        avatar: 'R',
        text: `${this.t('companyName')} ची औषधे वापरून मला चांगले परिणाम मिळाले. कोणतेही दुष्परिणाम जाणवले नाहीत. ही औषधे शरीराला ऊर्जा देतात आणि आत्मविश्वास वाढवतात. उंची वाढवण्यासाठी हा एक उत्तम आणि सुरक्षित पर्याय आहे. मी नक्कीच इतरांना याची शिफारस करेन.`
      },
      {
        id: 2,
        name: 'Priya Sharma',
        location: 'Mumbai, Maharashtra',
        avatar: 'P',
        text: `मी 6 महिने ${this.t('companyName')} ची औषधे वापरत आहे आणि मला खूप चांगले परिणाम दिसत आहेत. माझी उंची 2 इंच वाढली आहे आणि आता मला खूप आत्मविश्वास वाटतो. डॉक्टरांची सल्ला घेऊन वापरल्याने कोणतेही समस्या नाही.`
      },
      {
        id: 3,
        name: 'Amit Patel',
        location: 'Pune, Maharashtra',
        avatar: 'A',
        text: `${this.t('companyName')} च्या औषधांनी माझ्या मुलाची उंची वाढवण्यात खूप मदत झाली. आम्ही 1 वर्ष वापरल्यानंतर त्याची उंची 3 इंच वाढली आहे. औषधे नियमित घेतल्याने चांगले परिणाम मिळतात.`
      },
      {
        id: 4,
        name: 'Neha Deshmukh',
        location: 'Nagpur, Maharashtra',
        avatar: 'N',
        text: `मी आणि माझी बहीण दोघी ${this.t('companyName')} ची औषधे वापरत आहोत. आम्हाला दोघींना चांगले परिणाम मिळत आहेत. औषधे सुरक्षित आहेत आणि कोणतेही दुष्परिणाम नाहीत. आता आम्ही खूप आनंदी आहोत.`
      }
    ];
  }

  // Site content methods
  private loadSiteContent(): void {
    this.siteContentService.getHomeAssets().subscribe(assets => {
      this.homeAssets = assets;
      this.updateImagesForLanguage();
    });
  }

  private updateImagesForLanguage(): void {
    if (!this.homeAssets) return;
    
    // Get images for current language, fallback to English if not available
    const lang = this.currentLanguage;
    const bannerUrl = this.homeAssets.banner?.[lang]?.url || this.homeAssets.banner?.['en']?.url || '';
    const offerUrls = this.homeAssets.offers?.[lang]?.map(offer => offer.url) || 
                     this.homeAssets.offers?.['en']?.map(offer => offer.url) || [];
    
    this.bannerImageUrl = bannerUrl;
    this.offerImages = offerUrls;
    
    // Reset carousel when images change
    this.currentOfferIndex = 0;
    this.restartOfferCarousel();
  }

  // Offer carousel methods
  startOfferCarousel(): void {
    if (this.offerImages.length > 1) {
      this.offerCarouselInterval = setInterval(() => {
        this.nextOffer();
      }, 4000); // Change offer every 4 seconds
    }
  }

  stopOfferCarousel(): void {
    if (this.offerCarouselInterval) {
      clearInterval(this.offerCarouselInterval);
      this.offerCarouselInterval = null;
    }
  }

  restartOfferCarousel(): void {
    this.stopOfferCarousel();
    this.startOfferCarousel();
  }

  nextOffer(): void {
    if (this.offerImages.length > 1) {
      this.currentOfferIndex = (this.currentOfferIndex + 1) % this.offerImages.length;
    }
  }

  previousOffer(): void {
    if (this.offerImages.length > 1) {
      this.currentOfferIndex = this.currentOfferIndex === 0 
        ? this.offerImages.length - 1 
        : this.currentOfferIndex - 1;
    }
  }

  goToOffer(index: number): void {
    if (this.offerImages.length > 1 && index >= 0 && index < this.offerImages.length) {
      this.currentOfferIndex = index;
    }
  }

}
